/*
  File: cart-drawer-discounts.js
  Version: 1.0.0
  Date: 2026-04-28
  Purpose: Web Components for the cart-drawer discount surface —
    <cart-offer-list>      Eligible offers (list / cards variant).
    <cart-manual-code>     Collapsed → expanded form for known codes.
    <cart-applied-discounts> Chips above subtotal.
  Plus a subtotal transformer that toggles the "TOTAL / struck-through
  original / emerald discounted" footer state based on cart-state.

  All three components subscribe to window.cartState and unsubscribe on
  disconnect — they survive the cart drawer's wholesale innerHTML swap
  (the same constraint that drives cart-drawer-v4.js's delegated handlers).

  v1 scope: list variant fully built, cards variant gets functional but
  visually-minimal rendering (plan step 8 polishes it). 2-state manual-
  entry validation (success / generic failure) — see
  context/checkout-friction-reduction/sidecart_discounts_plan.md.

  v1 cap: window.cartState.MAX_DISCOUNT_CODES = 1 enforces single-code at
  the network layer. UI is multi-code-shaped end-to-end (looped renders,
  array membership checks) so lifting the cap is one constant change.
*/

(function () {
  'use strict';

  const ANIM_MS = 600;

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(cents) / 100);
    } catch (_) {
      return '$' + (Number(cents) / 100).toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ───────── Offers config ─────────
     Read once from #cart-discount-offers-config. Static for the page
     lifetime — if a merchandiser updates settings, the next pageload
     reflects them.  */

  let offersConfigCache = null;

  function readOffersConfig() {
    if (offersConfigCache) return offersConfigCache;
    const blob = document.getElementById('cart-discount-offers-config');
    if (!blob) {
      offersConfigCache = { offers: [], layout: 'list' };
      return offersConfigCache;
    }
    try {
      offersConfigCache = JSON.parse(blob.textContent || '{}');
    } catch (e) {
      console.error('[cart-discounts] failed to parse offers config', e);
      offersConfigCache = { offers: [], layout: 'list' };
    }
    if (!Array.isArray(offersConfigCache.offers)) offersConfigCache.offers = [];
    return offersConfigCache;
  }

  /* ───────── Per-offer eligibility ───────── */

  /**
   * @param {{minSpend:number, minItems:number}} offer
   * @param {number} subtotalCents — pre-discount items subtotal
   * @param {number} itemCount
   * @returns {{state:'eligible'|'locked', progress?:number, reason?:string}}
   */
  function offerStatus(offer, subtotalCents, itemCount) {
    const minSpendCents = (Number(offer.minSpend) || 0) * 100;
    const minItems = Number(offer.minItems) || 0;
    if (minSpendCents > 0 && subtotalCents < minSpendCents) {
      const dollarsShort = Math.ceil((minSpendCents - subtotalCents) / 100);
      return {
        state: 'locked',
        progress: subtotalCents / minSpendCents,
        reason: 'Spend $' + dollarsShort + ' more to unlock',
      };
    }
    if (minItems > 0 && itemCount < minItems) {
      const itemsShort = minItems - itemCount;
      return {
        state: 'locked',
        progress: itemCount / minItems,
        reason: 'Add ' + itemsShort + ' more to qualify',
      };
    }
    return { state: 'eligible' };
  }

  /* ───────── Animated number ─────────
     Simple rAF tween for the subtotal countdown. Cancellable so rapid
     cart mutations don't stack tweens. */

  function tweenNumber(el, fromCents, toCents, ms) {
    if (el.__cdTweenRaf) cancelAnimationFrame(el.__cdTweenRaf);
    if (fromCents === toCents) {
      el.textContent = formatMoney(toCents);
      return;
    }
    const start = performance.now();
    const delta = toCents - fromCents;
    function step(now) {
      const t = Math.min(1, (now - start) / ms);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = Math.round(fromCents + delta * eased);
      el.textContent = formatMoney(cur);
      if (t < 1) {
        el.__cdTweenRaf = requestAnimationFrame(step);
      } else {
        el.__cdTweenRaf = null;
      }
    }
    el.__cdTweenRaf = requestAnimationFrame(step);
  }

  /* ───────── <cart-offer-list> ───────── */

  class CartOfferList extends HTMLElement {
    connectedCallback() {
      this.__track = this.querySelector('[data-offers-track]');
      // Per-code error map and currently-applying code. Both are ephemeral
      // per-instance state — not persisted in cart-state since they're
      // UI-only (the underlying cart truth is whether the code is in
      // appliedDiscountCodes; errors are a presentation overlay).
      this.__offerErrors = Object.create(null);
      this.__applyingCode = null;
      // Last cart shape we rendered against, for auto-clear-on-mutation.
      // We watch subtotalCents (= totalPriceCents + totalDiscountsCents,
      // i.e. pre-discount original) and itemCount because both stay
      // stable across discount toggles but change on real cart edits.
      this.__lastSubtotalCents = null;
      this.__lastItemCount = null;

      this.__onState = (state) => this.render(state);
      this.__unsub = window.cartState && window.cartState.subscribe(this.__onState);
      this.__onClick = (e) => this.handleClick(e);
      this.addEventListener('click', this.__onClick);
      if (window.cartState) this.render(window.cartState.getState());
    }

    disconnectedCallback() {
      if (this.__unsub) this.__unsub();
      this.removeEventListener('click', this.__onClick);
    }

    render(state) {
      if (!this.__track) return;
      const config = readOffersConfig();
      const offers = config.offers || [];
      if (!offers.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const subtotalCents = state.totalPriceCents + state.totalDiscountsCents;
      const itemCount = state.itemCount;

      // Auto-clear errors when the cart materially changes (item added/
      // removed/qty changed). Do NOT clear when only the discount-related
      // fields shifted (those keep subtotalCents/itemCount stable). Skip
      // the very first render (lastX is null) so we don't wipe an error
      // that was just set then immediately re-rendered by the same emit.
      if (
        this.__lastSubtotalCents !== null &&
        (this.__lastSubtotalCents !== subtotalCents || this.__lastItemCount !== itemCount)
      ) {
        this.__offerErrors = Object.create(null);
      }
      this.__lastSubtotalCents = subtotalCents;
      this.__lastItemCount = itemCount;

      const applied = state.appliedDiscountCodes || [];
      this.__totalSavingsCents = state.totalDiscountsCents;
      const html = offers.map((o) => this.renderRow(o, subtotalCents, itemCount, applied)).join('');
      this.__track.innerHTML = html;
    }

    renderRow(offer, subtotalCents, itemCount, applied) {
      const code = String(offer.code || '').toUpperCase();
      const isApplied = applied.indexOf(code) !== -1;
      const errorMsg = this.__offerErrors[code] || '';
      const isErrored = !!errorMsg;
      const status = offerStatus(offer, subtotalCents, itemCount);
      let state = status.state;
      if (isApplied) state = 'applied';
      if (isErrored && !isApplied) state = 'error';

      const valueLabel = offer.type === 'percent' ? offer.value + '' : '$' + offer.value;
      const unitLabel = offer.type === 'percent' ? '% OFF' : 'OFF';

      let actionHtml;
      if (state === 'error') {
        actionHtml =
          '<button type="button" class="cd-offer__action cd-offer__action--dismiss" data-offer-dismiss="' +
          escapeHtml(code) +
          '" aria-label="Dismiss error">' +
          '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"/>' +
          '<line x1="6" y1="6" x2="18" y2="18"/>' +
          '</svg>' +
          '</button>';
      } else if (state === 'applied') {
        actionHtml =
          '<button type="button" class="cd-offer__action cd-offer__action--remove" data-offer-remove="' +
          escapeHtml(code) +
          '" aria-label="Remove ' +
          escapeHtml(code) +
          '">' +
          '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"/>' +
          '<line x1="6" y1="6" x2="18" y2="18"/>' +
          '</svg>' +
          'Remove' +
          '</button>';
      } else if (state === 'eligible') {
        actionHtml =
          '<button type="button" class="cd-offer__action" data-offer-apply="' +
          escapeHtml(code) +
          '">Apply</button>';
      } else {
        actionHtml = '<button type="button" class="cd-offer__action" disabled>Locked</button>';
      }

      // For single-code v1, the per-code amount equals total savings.
      // Multi-code stacking will need per-code allocations (cart.discountAllocations
      // via Storefront API) — see sidecart_discounts_plan.md "Future work".
      const savedInlineHtml =
        state === 'applied'
          ? '<span class="cd-offer__saved"> · Saved ' + formatMoney(this.__totalSavingsCents || 0) + '</span>'
          : '';

      const sublabelHtml = offer.sublabel
        ? '<span class="cd-offer__sublabel">' + escapeHtml(offer.sublabel) + '</span>'
        : '';
      const expiresHtml = offer.expiresText
        ? '<span class="cd-offer__expires">' + escapeHtml(offer.expiresText) + '</span>'
        : '';
      const badgeHtml = offer.badge
        ? '<span class="cd-offer__badge">' + escapeHtml(offer.badge) + '</span>'
        : '';
      const lockedDetail =
        state === 'locked'
          ? '<div class="cd-offer__progress" aria-hidden="true"><span class="cd-offer__progress-bar" style="width:' +
            Math.min(100, Math.max(0, (status.progress || 0) * 100)).toFixed(1) +
            '%"></span></div>' +
            '<span class="cd-offer__reason">' +
            escapeHtml(status.reason || '') +
            '</span>'
          : '';

      const errorDetail =
        state === 'error'
          ? '<div class="cd-offer__error" role="alert" aria-live="assertive">' +
            '<svg class="cd-offer__error-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<circle cx="12" cy="12" r="10"/>' +
            '<line x1="12" y1="8" x2="12" y2="12"/>' +
            '<line x1="12" y1="16" x2="12.01" y2="16"/>' +
            '</svg>' +
            '<span class="cd-offer__error-text">' +
            escapeHtml(errorMsg) +
            '</span>' +
            '</div>'
          : '';

      return (
        '<div class="cd-offer" data-state="' +
        state +
        '" data-offer-code="' +
        escapeHtml(code) +
        '">' +
        '<div class="cd-offer__icon" aria-hidden="true">' +
        '<span class="cd-offer__icon-value">' +
        escapeHtml(valueLabel) +
        '</span>' +
        '<span class="cd-offer__icon-unit">' +
        unitLabel +
        '</span>' +
        '</div>' +
        '<div class="cd-offer__body">' +
        '<p class="cd-offer__title">' +
        escapeHtml(offer.title || '') +
        '</p>' +
        '<div class="cd-offer__meta">' +
        '<span class="cd-offer__code">' +
        escapeHtml(code) +
        '</span>' +
        savedInlineHtml +
        badgeHtml +
        '</div>' +
        sublabelHtml +
        expiresHtml +
        lockedDetail +
        errorDetail +
        '</div>' +
        actionHtml +
        '</div>'
      );
    }

    handleClick(e) {
      // Dismiss × on an errored row — clears the error and re-renders.
      // stopPropagation so a future "row body click = retry" won't fire.
      const dismissBtn = e.target.closest('[data-offer-dismiss]');
      if (dismissBtn) {
        e.preventDefault();
        e.stopPropagation();
        const code = dismissBtn.getAttribute('data-offer-dismiss');
        if (code && this.__offerErrors[code]) {
          delete this.__offerErrors[code];
          if (window.cartState) this.render(window.cartState.getState());
        }
        return;
      }

      // No implicit retry. If the row is in the error state, the body is
      // inert — only the dismiss × responds. Treat any other click on an
      // errored row as a no-op.
      const errorRow = e.target.closest('.cd-offer[data-state="error"]');
      if (errorRow) {
        e.preventDefault();
        return;
      }

      const applyBtn = e.target.closest('[data-offer-apply]');
      if (applyBtn) {
        e.preventDefault();
        const code = applyBtn.getAttribute('data-offer-apply');
        if (!code || !window.cartState || this.__applyingCode) return;

        this.__applyingCode = code;
        // Clear any prior error for this code before retrying.
        if (this.__offerErrors[code]) delete this.__offerErrors[code];
        applyBtn.disabled = true;

        window.cartState.applyDiscount(code).then((result) => {
          this.__applyingCode = null;
          if (!result.ok) {
            // AJAX cart returns no granular reason — copy is intentionally
            // gentle and transient ("right now") per the design spec.
            this.__offerErrors[code] =
              "Couldn't apply this code right now. Try again.";
            // Force re-render with current cart state so the row flips
            // to error chrome. cart-state didn't actually change so the
            // subscriber wouldn't fire on its own.
            this.render(window.cartState.getState());
          }
          // On success, cart-state's emit will trigger our subscriber and
          // the row will render in 'applied' state automatically.
        });
        return;
      }

      const removeBtn = e.target.closest('[data-offer-remove]');
      if (removeBtn) {
        e.preventDefault();
        const code = removeBtn.getAttribute('data-offer-remove');
        if (code && window.cartState) {
          removeBtn.disabled = true;
          window.cartState.removeDiscount(code).then(() => {
            removeBtn.disabled = false;
          });
        }
      }
    }
  }

  if (!customElements.get('cart-offer-list')) {
    customElements.define('cart-offer-list', CartOfferList);
  }

  /* ───────── <cart-manual-code> ───────── */

  class CartManualCode extends HTMLElement {
    connectedCallback() {
      this.__form = this.querySelector('[data-manual-form]');
      this.__input = this.querySelector('[data-manual-input]');
      this.__submit = this.querySelector('[data-manual-submit]');
      this.__feedback = this.querySelector('[data-manual-feedback]');

      this.__onClick = (e) => this.handleClick(e);
      this.__onInput = () => this.handleInput();
      this.__onKeydown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleSubmit(e);
        }
      };
      // Stop change/input events on the discount-code text input from
      // bubbling up to <cart-drawer-items>. CartItems (assets/cart.js)
      // listens for `change` on its descendants; its key-based onChange
      // now bails when data-line-key is missing, but the bubble-stop
      // remains as defense in depth so a future change to onChange
      // can't silently drag the discount input into the cart-line flow.
      this.__stopBubble = (e) => e.stopPropagation();

      this.addEventListener('click', this.__onClick);
      if (this.__input) {
        this.__input.addEventListener('input', this.__onInput);
        this.__input.addEventListener('keydown', this.__onKeydown);
        this.__input.addEventListener('change', this.__stopBubble);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.__onClick);
      if (this.__input) {
        this.__input.removeEventListener('input', this.__onInput);
        this.__input.removeEventListener('keydown', this.__onKeydown);
        this.__input.removeEventListener('change', this.__stopBubble);
      }
    }

    handleClick(e) {
      const toggle = e.target.closest('[data-manual-toggle]');
      if (toggle) {
        this.expand();
        return;
      }
      const cancel = e.target.closest('[data-manual-cancel]');
      if (cancel) {
        this.collapse();
        return;
      }
      const submit = e.target.closest('[data-manual-submit]');
      if (submit && !submit.disabled) {
        this.handleSubmit(e);
      }
    }

    expand() {
      this.dataset.state = 'expanded';
      const trigger = this.querySelector('[data-manual-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (this.__input) {
        // microtask so the form is visible before focus tries to scroll it in
        Promise.resolve().then(() => this.__input.focus());
      }
    }

    collapse() {
      this.dataset.state = 'collapsed';
      this.clearFeedback();
      if (this.__input) this.__input.value = '';
      this.updateSubmitDisabled();
      const trigger = this.querySelector('[data-manual-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    handleInput() {
      if (!this.__input) return;
      const upper = this.__input.value.toUpperCase();
      if (this.__input.value !== upper) this.__input.value = upper;
      this.clearFeedback();
      this.updateSubmitDisabled();
    }

    updateSubmitDisabled() {
      if (!this.__submit || !this.__input) return;
      this.__submit.disabled = !this.__input.value.trim();
    }

    handleSubmit(e) {
      e.preventDefault();
      if (!this.__input || !window.cartState) return;
      const code = this.__input.value.trim().toUpperCase();
      if (!code) return;
      this.__submit.disabled = true;
      // Pending state pre-occupies the feedback slot in emerald so that
      // success or error animates by replacing it (no layout shift).
      this.showFeedback('pending', 'Applying…');

      window.cartState
        .applyDiscount(code)
        .then((result) => {
          if (result.ok) {
            this.showFeedback('success', code + ' applied.');
            this.__input.value = '';
          } else if (result.reason === 'already_applied') {
            this.showFeedback('error', code + ' is already applied.');
          } else if (result.reason === 'cap_reached') {
            // v1 single-code cap — multi-code lift removes this branch.
            this.showFeedback('error', "Only one code can be applied at a time. Remove the current code first.");
          } else {
            this.showFeedback('error', this.dataset.errorMessage || 'Unable to apply code to your cart');
          }
          this.updateSubmitDisabled();
        });
    }

    showFeedback(kind, message) {
      if (!this.__feedback) return;
      this.dataset.feedback = kind;
      this.__feedback.dataset.feedbackKind = kind;
      let icon;
      if (kind === 'success') {
        icon = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
      } else if (kind === 'pending') {
        // Tiny circular SVG spinner — strokes the top half via stroke-dasharray
        // and rotates via CSS animation (see cd-manual__spinner in CSS).
        icon = '<svg class="cd-manual__spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke-dasharray="42 42"/></svg>';
      } else {
        icon = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
      }
      this.__feedback.innerHTML = icon + '<span>' + escapeHtml(message) + '</span>';
      this.__feedback.hidden = false;
      this.__feedback.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    }

    clearFeedback() {
      if (!this.__feedback) return;
      this.dataset.feedback = '';
      this.__feedback.dataset.feedbackKind = '';
      this.__feedback.innerHTML = '';
      this.__feedback.hidden = true;
    }
  }

  if (!customElements.get('cart-manual-code')) {
    customElements.define('cart-manual-code', CartManualCode);
  }

  /* ───────── <cart-applied-discounts> ─────────
     Chips above the subtotal row. Renders from cart-state on every
     change. Server-rendered fallback markup is replaced on first hydrate. */

  /* Milestone codes are loaded once from the boot snippet's config blob.
     Chips matching one of these codes render with --milestone modifier and
     no × button (the progress bar owns their lifecycle).  */
  let milestoneCodesCache = null;
  function getMilestoneCodes() {
    if (milestoneCodesCache) return milestoneCodesCache;
    const blob = document.getElementById('cart-progress-milestone-codes');
    if (!blob) {
      milestoneCodesCache = new Set();
      return milestoneCodesCache;
    }
    try {
      const parsed = JSON.parse(blob.textContent || '{}');
      const codes = Array.isArray(parsed.codes) ? parsed.codes : [];
      milestoneCodesCache = new Set(codes.map((c) => String(c).toUpperCase()));
    } catch (e) {
      console.error('[cart-discounts] failed to parse milestone codes', e);
      milestoneCodesCache = new Set();
    }
    return milestoneCodesCache;
  }

  /* Virtual milestone chips — synthesized client-side from milestone state
     for rewards Shopify doesn't allocate as a discount_application (free
     shipping is the canonical case; Shopify computes shipping at checkout
     so the cart payload has no entry to chip). Read once from the config
     blob; merged with real applied discounts at render time. */
  let virtualMilestonesCache = null;
  function getVirtualMilestones() {
    if (virtualMilestonesCache) return virtualMilestonesCache;
    const blob = document.getElementById('cart-progress-virtual-chips');
    if (!blob) {
      virtualMilestonesCache = [];
      return virtualMilestonesCache;
    }
    try {
      const parsed = JSON.parse(blob.textContent || '{}');
      virtualMilestonesCache = Array.isArray(parsed.milestones)
        ? parsed.milestones.filter((m) => Number(m.threshold) > 0 && String(m.label || '').trim())
        : [];
    } catch (e) {
      console.error('[cart-discounts] failed to parse virtual-chip config', e);
      virtualMilestonesCache = [];
    }
    return virtualMilestonesCache;
  }

  class CartAppliedDiscounts extends HTMLElement {
    connectedCallback() {
      this.__onState = (state) => this.render(state);
      this.__unsub = window.cartState && window.cartState.subscribe(this.__onState);
      this.__onClick = (e) => this.handleClick(e);
      this.addEventListener('click', this.__onClick);
      if (window.cartState) this.render(window.cartState.getState());
    }

    disconnectedCallback() {
      if (this.__unsub) this.__unsub();
      this.removeEventListener('click', this.__onClick);
    }

    render(state) {
      const realDiscounts = state.appliedDiscounts || [];
      // Synthesize virtual chips for any threshold-met milestone whose
      // chip is enabled and whose label isn't already represented by a
      // real applied discount (avoid double-chip when EG / another app
      // titles its discount the same as the milestone label).
      const totalCents = state.totalPriceCents || 0;
      const realTitles = new Set(
        realDiscounts.map((d) => String(d.title || '').trim().toLowerCase())
      );
      const virtualChips = getVirtualMilestones()
        .filter((m) => totalCents >= Number(m.threshold))
        .filter((m) => !realTitles.has(String(m.label).trim().toLowerCase()))
        .map((m) => ({
          key: 'virtual:' + m.label,
          title: m.label,
          type: 'virtual_milestone',
          targetType: 'line_item',
          amountCents: 0,
          removable: false,
          isVirtual: true,
        }));
      const discounts = realDiscounts.concat(virtualChips);
      if (!discounts.length) {
        this.innerHTML = '';
        return;
      }
      // Header shows the cart-affecting savings total (line-item discounts
      // only — shipping_line allocations are excluded so the math matches
      // the visible subtotal). Chips render for ALL discount types so
      // automatic / manual / script / shipping discounts are visible to
      // the customer; only type='discount_code' on line items gets a
      // removable × button.
      const totalSavings = state.totalDiscountsCents;
      const headerHtml =
        totalSavings > 0
          ? '<div class="cd-applied__header">' +
            '<span class="cd-applied__eyebrow">' +
            '<svg class="cd-applied__sparkle" width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M12 2l1.6 5.4 5.4 1.6-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6z"/>' +
            '<circle cx="19" cy="4" r="1"/>' +
            '<circle cx="20" cy="18" r="1.2"/>' +
            '</svg>' +
            'You saved' +
            '</span>' +
            '<span class="cd-applied__total">−' +
            formatMoney(totalSavings) +
            '</span>' +
            '</div>'
          : '';

      const milestoneCodes = getMilestoneCodes();
      const chipsHtml =
        '<ul class="cd-applied__list" role="list" data-applied-list>' +
        discounts
          .map((d) => {
            const isMilestone =
              d.isVirtual ||
              (d.type === 'discount_code' && milestoneCodes.has(d.key));
            // Milestone chips are non-removable — the progress bar manages
            // their lifecycle; a manual × would create earned-but-applied
            // drift the reconciler would just undo on the next update.
            const showRemove = d.removable && !isMilestone;
            const removeBtn = showRemove
              ? '<button type="button" class="cd-applied__remove" data-applied-remove="' +
                escapeHtml(d.key) +
                '" aria-label="Remove discount code ' +
                escapeHtml(d.title) +
                '">' +
                '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                '<line x1="18" y1="6" x2="6" y2="18"/>' +
                '<line x1="6" y1="6" x2="18" y2="18"/>' +
                '</svg>' +
                '</button>'
              : '';
            const earnedIcon = isMilestone
              ? '<svg class="cd-applied__earned-icon" width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
                '<path d="M12 2l1.6 5.4 5.4 1.6-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6z"/>' +
                '</svg>'
              : '';
            return (
              '<li class="cd-applied__chip' +
              (isMilestone ? ' cd-applied__chip--milestone' : '') +
              '" data-applied-code="' +
              escapeHtml(d.key) +
              '" data-discount-type="' +
              escapeHtml(d.type) +
              '"' +
              (isMilestone ? ' data-milestone="1"' : '') +
              '>' +
              earnedIcon +
              '<span class="cd-applied__title">' +
              escapeHtml(d.title) +
              '</span>' +
              removeBtn +
              '</li>'
            );
          })
          .join('') +
        '</ul>';

      this.innerHTML = headerHtml + chipsHtml;
    }

    handleClick(e) {
      const btn = e.target.closest('[data-applied-remove]');
      if (!btn) return;
      e.preventDefault();
      const code = btn.getAttribute('data-applied-remove');
      if (code && window.cartState) {
        btn.disabled = true;
        window.cartState.removeDiscount(code);
      }
    }
  }

  if (!customElements.get('cart-applied-discounts')) {
    customElements.define('cart-applied-discounts', CartAppliedDiscounts);
  }

  /* ───────── Subtotal-row transformer ─────────
     Toggles .cd-footer__row--has-discount on the existing subtotal row,
     injects/updates the strike-through original price element, and
     animates the discounted price via tweenNumber. The pre-existing
     subtotal subscriber in cart-drawer-v4.js continues to update
     [data-cart-total] elements as before — this is a layer ON TOP. */

  function ensureOriginalPriceEl(row) {
    let el = row.querySelector('.cd-footer__total-original');
    if (!el) {
      el = document.createElement('span');
      el.className = 'cd-footer__total-original';
      el.setAttribute('aria-hidden', 'true');
      const totalEl = row.querySelector('[data-cart-total]');
      if (totalEl) row.insertBefore(el, totalEl);
      else row.appendChild(el);
    }
    return el;
  }

  function updateSubtotalRow(state) {
    // Find the footer subtotal row. Drawer re-renders may swap it; re-query each time.
    const rows = document.querySelectorAll('.cd-footer__row');
    rows.forEach((row) => {
      const totalEl = row.querySelector('[data-cart-total]');
      if (!totalEl) return;

      // Subtotal transform keys off the savings amount, not the code
      // count — automatic and manual discounts also reduce total_price
      // and should drive the SUBTOTAL → TOTAL flip even though they
      // aren't codes the customer entered.
      const hasDiscount = (state.totalDiscountsCents || 0) > 0;
      const originalCents = state.totalPriceCents + state.totalDiscountsCents;

      if (hasDiscount) {
        row.classList.add('cd-footer__row--has-discount');
        const origEl = ensureOriginalPriceEl(row);
        origEl.textContent = formatMoney(originalCents);

        // Animate the discounted total. Read current textContent's prior
        // value via a stashed dataset key so we ease from prev→new even
        // across drawer re-renders.
        const prevCents = Number(totalEl.dataset.cdPrevCents);
        const fromCents = Number.isFinite(prevCents) ? prevCents : originalCents;
        tweenNumber(totalEl, fromCents, state.totalPriceCents, ANIM_MS);
        totalEl.dataset.cdPrevCents = String(state.totalPriceCents);
      } else {
        row.classList.remove('cd-footer__row--has-discount');
        const origEl = row.querySelector('.cd-footer__total-original');
        if (origEl) origEl.remove();
        totalEl.dataset.cdPrevCents = String(state.totalPriceCents);
      }
    });
  }

  if (window.cartState && !window.cartState.__cdDiscountSubtotalSubscribed) {
    window.cartState.__cdDiscountSubtotalSubscribed = true;
    window.cartState.subscribe(updateSubtotalRow);
    // Initial paint — subscribe doesn't fire immediately.
    updateSubtotalRow(window.cartState.getState());
  }

  /* ───────── Cookie discount auto-promote ─────────
     Shopify's /discount/CODE URL sets a `discount_code` session cookie
     but does NOT attach the code to cart.discount_codes — per Shopify's
     own docs the discount only applies at checkout, not in the cart UI.
     If we don't surface it, the customer sees a cart with no chip and
     no savings, then a sudden discount at checkout.

     Promote the cookie value into a real cart-level discount via
     /cart/update.js (same path as manual entry / offer apply). After
     this, cart.discount_codes contains the code, the chip renders, and
     totals reflect the discount in the drawer.

     Runs once per page load. If the cookie code is already on the cart
     (because the user previously applied it), the dedupe inside
     applyDiscount() short-circuits with reason='already_applied'. */
  function readDiscountCookie() {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    if (!m) return '';
    try {
      return decodeURIComponent(m[1]).trim().toUpperCase();
    } catch (_) {
      return '';
    }
  }

  function clearDiscountCookie() {
    // Path / domain must match what Shopify set the cookie with for
    // the deletion to actually delete (browsers compare). Shopify's
    // /discount/CODE flow sets it on path=/, no domain attr (defaults
    // to host-only).
    document.cookie =
      'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
  }

  if (window.cartState && !window.cartState.__cdCookieDiscountPromoted) {
    window.cartState.__cdCookieDiscountPromoted = true;
    const code = readDiscountCookie();
    if (code) {
      const current = window.cartState.getState();
      const alreadyApplied = (current.appliedDiscountCodes || []).indexOf(code) !== -1;
      if (!alreadyApplied) {
        // Fire-and-forget. Don't await — clear the cookie either way
        // so this can't keep re-firing on every navigation. If the
        // server ultimately rejects the code, the user can re-enter
        // it manually; not retrying silently on every page is the
        // more important UX.
        window.cartState.applyDiscount(code);
      }
      // Always clear after the first attempt — whether the code was
      // already applied (no-op) or we just promoted it. Prevents the
      // drawer from re-opening every page load when a removed code
      // would otherwise be auto-re-applied.
      clearDiscountCookie();
    }
  }
})();
